const CART_KEY = 'ennix_cart';
const CART_IDS_KEY = 'ennix_cart_ids';
const CART_COUNT_KEY = 'ennix_cart_count';
export const CART_EVENT = 'ennix_cart_updated';

// ─── In-memory store for logged-in users (no localStorage) ───────────────────
let _serverCartIds = new Set();
let _serverCartInitPromise = null;
let _serverCartInitialized = false;

export const setServerCartIds = (ids = []) => {
    _serverCartIds = new Set(ids.filter(Boolean).map(String));
    _serverCartInitialized = true;
};

export const isInServerCart = (productId) =>
    productId ? _serverCartIds.has(String(productId)) : false;

export const addServerCartId = (productId) => {
    if (productId) _serverCartIds.add(String(productId));
};

export const removeServerCartId = (productId) => {
    if (productId) _serverCartIds.delete(String(productId));
};

/** Fetches cart IDs from server once per page load for logged-in users. */
export const initServerCart = () => {
    if (_serverCartInitPromise) return _serverCartInitPromise;
    _serverCartInitPromise = fetch('/api/user/cart')
        .then((r) => r.json())
        .then((json) => {
            const raw = json?.data?.items ?? json?.items ?? json?.data ?? [];
            const ids = raw
                .map((i) => i.productId ?? i._id ?? i.product?._id)
                .filter(Boolean);
            setServerCartIds(ids);
            dispatchCartUpdate();
        })
        .catch(() => { _serverCartInitialized = true; });
    return _serverCartInitPromise;
};

export const resetServerCartInit = () => {
    _serverCartInitPromise = null;
    _serverCartIds = new Set();
    _serverCartInitialized = false;
    dispatchCartUpdate();
};
// ─────────────────────────────────────────────────────────────────────────────

export const dispatchCartUpdate = () => {
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(CART_EVENT));
    }
};

export const getCartCount = () => {
    if (typeof window === 'undefined') return 0;
    // Once server data is loaded (even empty cart), trust it exclusively.
    // Prevents stale localStorage counts from showing for logged-in users.
    if (_serverCartInitialized) return _serverCartIds.size;
    const stored = localStorage.getItem(CART_COUNT_KEY);
    if (stored !== null) return Number(stored);
    return getLocalCart().reduce((sum, item) => sum + (item.quantity || 1), 0);
};

export const setCartCount = (n) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem(CART_COUNT_KEY, String(n));
    }
};

export const getLocalCart = () => {
    if (typeof window === 'undefined') return [];
    try {
        return JSON.parse(localStorage.getItem(CART_KEY) || '[]');
    } catch {
        return [];
    }
};

export const addToLocalCart = (item) => {
    const cart = getLocalCart();
    const existing = cart.find((c) => c.productId === item.productId);
    if (existing) {
        existing.quantity += item.quantity || 1;
    } else {
        cart.push({ ...item, quantity: item.quantity || 1 });
    }
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    const total = cart.reduce((s, c) => s + (c.quantity || 1), 0);
    localStorage.setItem(CART_COUNT_KEY, String(total));
    dispatchCartUpdate();
};

export const removeFromLocalCart = (productId) => {
    const cart = getLocalCart().filter((c) => c.productId !== productId);
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
    const total = cart.reduce((s, c) => s + (c.quantity || 1), 0);
    localStorage.setItem(CART_COUNT_KEY, String(total));
    dispatchCartUpdate();
};

// Guest-only helpers (ennix_cart_ids) ─────────────────────────────────────────
export const isProductInCart = (productId) => {
    if (typeof window === 'undefined' || !productId) return false;
    try {
        const ids = JSON.parse(localStorage.getItem(CART_IDS_KEY) || '[]');
        return ids.includes(productId);
    } catch {
        return false;
    }
};

export const markProductInCart = (productId) => {
    if (typeof window === 'undefined' || !productId) return;
    try {
        const ids = JSON.parse(localStorage.getItem(CART_IDS_KEY) || '[]');
        if (!ids.includes(productId)) ids.push(productId);
        localStorage.setItem(CART_IDS_KEY, JSON.stringify(ids));
    } catch {}
};

export const unmarkProductFromCart = (productId) => {
    if (typeof window === 'undefined' || !productId) return;
    try {
        const ids = JSON.parse(localStorage.getItem(CART_IDS_KEY) || '[]').filter((id) => id !== productId);
        localStorage.setItem(CART_IDS_KEY, JSON.stringify(ids));
    } catch {}
};
// ─────────────────────────────────────────────────────────────────────────────

export const clearLocalCart = () => {
    if (typeof window !== 'undefined') {
        localStorage.removeItem(CART_KEY);
        localStorage.removeItem(CART_IDS_KEY);
        localStorage.removeItem(CART_COUNT_KEY);
        dispatchCartUpdate();
    }
};

export const syncCartToServer = async (items) => {
    if (!items || items.length === 0) return;
    try {
        const res = await fetch('/api/user/sync-cart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items }),
        });
        if (res.ok) clearLocalCart();
    } catch { /* silent — cart still in localStorage if sync fails */ }
};
